<script setup lang="ts">
  import { cva } from 'class-variance-authority'

  const props = withDefaults(
    defineProps<{
      price: number
      size?: 'default' | 'lg'
      showPlus?: boolean
      showFraction?: boolean
      increaseFontSizeOnDesktop?: boolean
    }>(),
    {
      showPlus: false,
      size: 'default',
      showFraction: false,
      increaseFontSizeOnDesktop: false
    }
  )

  const priceParts = computed(() => {
    let whole = null
    let fraction = null

    if (!props.price) {
      return { whole, fraction }
    }

    if (props.showFraction) {
      const splitPrice = props.price.toFixed(2).toString().split('.')

      whole = splitPrice[0]
      fraction = splitPrice[1]

      return { whole, fraction }
    }

    whole = Math.round(props.price)

    return { whole }
  })

  const priceBase = computed(() =>
    cva('flex justify-center items-center', {
      variants: {
        size: {
          default: 'price-size--default',
          lg: 'price-size--lg'
        },
        increaseOnDesktop: {
          true: 'text-large-desktop',
          false: ''
        }
      }
    })({
      size: props.size,
      increaseOnDesktop: props.increaseFontSizeOnDesktop
    })
  )
</script>

<template>
  <div :class="[priceBase, increaseFontSizeOnDesktop]">
    <span v-if="showPlus">+</span>
    <span> {{ priceParts.whole }}</span>
    <sup v-if="showFraction">{{ priceParts.fraction }}</sup>
    <span :class="{ 'pl-0.5': !showFraction }">€</span>
  </div>
</template>

<style scoped lang="scss">
  .price {
    &-size {
      &--default {
        @apply text-xs;

        @screen md {
          @apply text-base;
        }

        &.text-large-desktop {
          @screen lg {
            @apply text-md leading-8;
          }
        }

        sup {
          font-size: 0.4375rem;
          line-height: 0.8125rem;
          top: -0.1rem;

          @screen md {
            font-size: 0.5625rem;
            top: -0.2rem;
          }
        }
      }
      &--lg {
        @apply text-xl font-medium leading-9;

        @screen md {
          @apply text-2xl font-medium leading-10;
        }

        sup {
          @apply text-subtitle-2;
          top: -0.3rem;

          @screen md {
            top: -0.5rem;
          }
        }
      }
    }
  }
</style>
